import React from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert, Chip, Breadcrumbs, Link, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSupportArticles } from "../../hooks/support/useSupportArticles";
import { useSupportArticleContent } from "../../hooks/support/useSupportArticleContent";
import NotionBlockRenderer from "../../components/NotionBlockRenderer";

function SupportArticleDetail() {
  const { articleId } = useParams<{ articleId: string }>();
  const { articles, isLoading: articlesLoading } = useSupportArticles();
  const { blocks, isLoading: contentLoading, isError } = useSupportArticleContent(articleId || null);

  const article = articles.find((a) => a.id === articleId);

  if (articlesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Article not found.</Alert>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load article content. Please try again later.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "800px", mx: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton component={RouterLink} to="/support" aria-label="Back to Support">
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs>
          <Link component={RouterLink} to="/support" underline="hover">
            Support Center
          </Link>
          <Typography color="text.primary">{article.category}</Typography>
          <Typography color="text.primary">{article.title}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Article Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {article.title}
        </Typography>

        {/* Tags */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {article.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        {/* Last Modified */}
        <Typography variant="body2" color="text.secondary">
          Last updated:{" "}
          {new Date(article.lastModified).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>

      {/* Article Content */}
      {contentLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : blocks.length > 0 ? (
        <Box sx={{ mb: 4 }}>
          <NotionBlockRenderer blocks={blocks} />
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No content available for this article.
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ mt: 6, pt: 3, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Need more help?{" "}
          <Link component={RouterLink} to="/support">
            Browse other articles
          </Link>{" "}
          or contact our support team.
        </Typography>
      </Box>
    </Box>
  );
}

export default SupportArticleDetail;
